import type { ReactNode } from 'react';
import { DotsThree, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';
import { Popover } from '../ui/popover';

export interface TaskMenuItem {
  label: string;
  hint: string;
  icon: PhosphorIcon;
  onSelect: () => void;
  /** The one that says something went wrong: set apart, in red (Figma "Report a problem"). */
  danger?: boolean;
}

/**
 * The "⋯" of the client's task page (Figma C03.3 menu): each entry has a tile, a name and a line saying what it does.
 * It is drawn over the page, so nothing around it can clip it.
 */
export function TaskMenu({ items, trigger }: { items: TaskMenuItem[]; trigger?: ReactNode }) {
  return (
    <Popover
      role="menu"
      className="w-(--mbm-layout-task-menu-width) py-(--mbm-space-2)"
      trigger={(props) => (
        <button
          type="button"
          aria-label="More actions"
          {...props}
          className="flex size-(--mbm-size-icon-button) items-center justify-center rounded-full border border-border-line bg-surface-card text-text-primary shadow-(--mbm-shadow-sm) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          {trigger ?? <Icon icon={DotsThree} size="md" />}
        </button>
      )}
    >
      {(close) => (
        <ul className="flex flex-col">
          {items.map((item) => (
            <li key={item.label} className={cx(item.danger && 'mt-px border-t border-border-line')}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  item.onSelect();
                }}
                className="flex w-full items-center gap-(--mbm-space-3) px-(--mbm-space-3) py-(--mbm-space-2) text-left hover:bg-surface-tint focus-visible:bg-surface-tint focus-visible:outline-none"
              >
                <span
                  className={cx(
                    'flex size-(--mbm-size-icon-tile) shrink-0 items-center justify-center rounded-(--mbm-component-radius-icon-tile)',
                    item.danger ? 'bg-feedback-error-bg text-feedback-error' : 'bg-surface-tint text-text-primary',
                  )}
                >
                  <Icon icon={item.icon} size="md" />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className={cx('text-label-large', item.danger ? 'text-feedback-error' : 'text-text-primary')}>
                    {item.label}
                  </span>
                  <span className="text-body-xsmall text-text-tertiary">{item.hint}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Popover>
  );
}
